import { Injectable } from '@angular/core'
import { map, Observable, catchError } from 'rxjs'
import { PersonaData, mappingPersona, mappingPersonas } from './persona.mapping'
import {
  GetPersonasGQL,
  GetPersonaByIdGQL,
  CreatePersonaGQL,
  UpdatePersonaGQL,
  DeletePersonaGQL,
  DeletePersonasGQL,
  ReorderPersonasGQL,
  CreatePersonaInput,
  UpdatePersonaInput,
} from '../../../_generated/graphql'
import { assertGraphqlData } from '../../util/assertGraphqlData'
import { throwIfCustomError } from '../../util/throwIfCustomError'

@Injectable({
  providedIn: 'root',
})
export class PersonaService {
  constructor(
    private getPersonasGQL: GetPersonasGQL,
    private getPersonaByIdGQL: GetPersonaByIdGQL,
    private createPersonaGQL: CreatePersonaGQL,
    private updatePersonaGQL: UpdatePersonaGQL,
    private deletePersonaGQL: DeletePersonaGQL,
    private deletePersonasGQL: DeletePersonasGQL,
    private reorderPersonasGQL: ReorderPersonasGQL,
  ) {}

  getPersonas(): Observable<PersonaData[]> {
    return this.getPersonasGQL.fetch().pipe(
      map((res) => {
        assertGraphqlData(res)
        return mappingPersonas(res.data.getPersonas)
      }),
      catchError((error) => {
        console.error('Failed to fetch personas:', error)
        throwIfCustomError(error)
        throw error
      }),
    )
  }

  getPersonaById(id: string): Observable<PersonaData | null> {
    return this.getPersonaByIdGQL
      .fetch({
        variables: { id },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((res) => {
          assertGraphqlData(res)
          const persona = res.data.getPersonaById
          return persona ? mappingPersona(persona) : null
        }),
        catchError((error) => {
          console.error(`Failed to fetch persona ${id}:`, error)
          throwIfCustomError(error)
          throw error
        }),
      )
  }

  createPersona(param: CreatePersonaInput) {
    return this.createPersonaGQL
      .mutate({
        variables: { param },
      })
      .pipe(
        catchError((error) => {
          console.error('Failed to create persona:', error)
          throw error
        }),
      )
  }

  updatePersona(param: UpdatePersonaInput) {
    return this.updatePersonaGQL
      .mutate({
        variables: { param },
      })
      .pipe(
        catchError((error) => {
          console.error('Failed to update persona:', error)
          throw error
        }),
      )
  }

  deletePersona(id: string) {
    return this.deletePersonaGQL.mutate({ variables: { id } }).pipe(
      catchError((error) => {
        console.error('Failed to delete persona:', error)
        throw error
      }),
    )
  }

  deletePersonas(ids: string[]) {
    return this.deletePersonasGQL.mutate({ variables: { ids } }).pipe(
      catchError((error) => {
        console.error('Failed to delete personas:', error)
        throw error
      }),
    )
  }

  reorderPersonas(ids: string[]) {
    return this.reorderPersonasGQL.mutate({ variables: { ids } }).pipe(
      catchError((error) => {
        console.error('Failed to reorder personas:', error)
        throwIfCustomError(error)
        throw error
      }),
    )
  }
}
